import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Phone, Award, Users, Car, Shield } from 'lucide-react';

const AboutUs = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const highlights = [
    { icon: Award, title: 'Established 2009', description: 'Over 15 years of excellence' },
    { icon: Users, title: 'Professional Drivers', description: 'Trained and licensed experts' },
    { icon: Car, title: 'Huge Fleet', description: 'Wide range of vehicles' },
    { icon: Shield, title: 'Safe & Reliable', description: 'Your safety is our priority' },
  ];

  return (
    <>
      <SEO
        title="About Us - Advait Tour and Travels | Leading Taxi Service in Chandigarh"
        description="Learn about Advait Tour and Travels, one of the leading tour and travel companies in Chandigarh. Established in 2009, providing best cab booking services."
        keywords="about advait tour and travels, chandigarh taxi company, tour and travel company, cab service chandigarh, transportation services"
        canonicalUrl={`${window.location?.origin}/about-us`}
        ogTitle="About Us - Advait Tour and Travels"
        ogDescription="Learn about Advait Tour and Travels, one of the leading tour and travel companies in Chandigarh since 2009."
        ogUrl={`${window.location?.origin}/about-us`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Advait Tour and Travels",
          "description": "Learn about Advait Tour and Travels, one of the leading tour and travel companies in Chandigarh since 2009."
        }}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-20 mb-12">
            <div className="container mx-auto px-4 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                About Us
              </h1>
              <p className="text-xl text-white max-w-3xl mx-auto">
                Your Trusted Travel Partner Since 2009
              </p>
            </div>
          </div>

          {/* Highlights Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {highlights?.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <highlight.icon className="text-blue-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{highlight?.title}</h3>
                  <p className="text-gray-600">{highlight?.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Content Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">About Advait Tour and Travels</h2>
              
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  <strong>Advait Tour and Travels</strong> is one of the leading tour and travel companies in Chandigarh. With an aim of fulfilling the transportation needs of the customers, we have been providing our clients the best cab booking services. No matter you want to book one way or round trip, we have solutions for all your traveling needs. Our goal is to make travelling smooth, easier and hassle-free for the customers. We have got a huge fleet of cars to choose from as per your needs.
                </p>

                <p>
                  Advait Tour and Travels was established in 2009 with an aim of solving the transportation problems of commuter's. Soon after our inception, we cemented our position strong on the market by providing our clients first-class transportation services. We are constantly upgrading our business practices to provide our customers with the highest-quality services.
                </p>

                <p>
                  With the strong backing of highly trained, experienced and licensed drivers, we can assure you that you will have a pleasant travelling experience with us. So, whenever you are on a personal, leisure or business trip, book a cab with us to get the best offers for your ride.
                </p>

                <p>
                  You can Pre-Book a cab with us to take off the stress about how you get to your next destination. At Advait Tour and Travels, you can be assured that you will be picked at the location of your choice. No matter you are travelling alone, or with a group of professionals, we provide excellent service to our customers. Possessing years of rich experience in serving travellers, we strive to offer our customers comfortable and safe transportation solutions.
                </p>

                <div className="bg-blue-50 rounded-lg p-6 mt-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">Our Mission</h3>
                  <p className="text-gray-700">
                    To provide reliable, comfortable, and affordable transportation solutions that exceed customer expectations while maintaining the highest standards of safety and professionalism.
                  </p>
                </div>

                <div className="bg-orange-50 rounded-lg p-6 mt-6">
                  <h3 className="text-2xl font-bold text-orange-900 mb-4">Why Choose Us?</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>15+ Years of Experience:</strong> Serving customers since 2009 with dedication and excellence</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>Professional Drivers:</strong> Highly trained, experienced, and licensed drivers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>Wide Fleet:</strong> Huge selection of vehicles to suit every need and budget</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>24/7 Service:</strong> Available round the clock for your convenience</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>Transparent Pricing:</strong> No hidden charges, clear and upfront pricing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>Customer Satisfaction:</strong> Committed to providing the best travel experience</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300"
              >
                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AboutUs;